import { useLayoutEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, Phone } from 'lucide-react';
import { gsap, revealUp } from '../lib/motion';
import { Seo, SITE, breadcrumbJsonLd } from '../lib/seo';
import { SERVICES, serviceBySlug, type Service } from '../content/services';
import NotFound from './NotFound';

function serviceJsonLd(s: Service) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: s.name,
    serviceType: s.name,
    description: s.metaDescription,
    url: `${SITE.origin}/services/${s.slug}`,
    provider: { '@id': `${SITE.origin}/#org` },
    areaServed: SITE.areaServed.map((name) => ({ '@type': 'Place', name })),
  };
}

function faqJsonLd(s: Service) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: s.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

export default function ServicePage() {
  const { slug } = useParams();
  const service = slug ? serviceBySlug(slug) : undefined;
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ctx = gsap.context(() => {
      revealUp('.sp-reveal', el);
    }, el);
    return () => ctx.revert();
  }, [slug]);

  if (!service) return <NotFound />;
  const s = service;
  const others = SERVICES.filter((o) => o.slug !== s.slug).slice(0, 3);

  return (
    <div ref={ref}>
      <Seo
        title={s.metaTitle}
        description={s.metaDescription}
        path={`/services/${s.slug}`}
        ogImage={s.heroImage}
        jsonLd={[
          serviceJsonLd(s),
          faqJsonLd(s),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/#services' },
            { name: s.name, path: `/services/${s.slug}` },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden vignette">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${s.heroImage}")` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-950/85 via-ink-950/60 to-ink-950" />
        <div className="relative z-10 mx-auto flex min-h-[68vh] max-w-7xl flex-col justify-end px-6 pb-20 pt-40 md:px-10 md:pb-28">
          <p className="sp-reveal eyebrow mb-6">{s.eyebrow}</p>
          <h1 className="sp-reveal max-w-4xl font-display text-5xl font-medium leading-[1.05] text-steel-100 md:text-7xl">
            {s.h1} <span className="italic text-gold-300">{s.h1Accent}</span>
          </h1>
          <div className="sp-reveal mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              className="bg-gold-500 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink-950 transition-all duration-500 hover:bg-gold-400"
            >
              Request a Bid
            </Link>
            <a
              href={`tel:${SITE.phone}`}
              className="inline-flex items-center gap-3 border border-gold-500/40 px-8 py-4 text-xs uppercase tracking-[0.25em] text-gold-300 transition-all duration-300 hover:bg-gold-500 hover:text-ink-950"
            >
              <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* Intro + included */}
      <section className="bg-ink-950 py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {s.intro.map((p) => (
              <p
                key={p.slice(0, 32)}
                className="sp-reveal mb-6 max-w-2xl text-lg font-light leading-relaxed text-steel-300 md:text-xl"
              >
                {p}
              </p>
            ))}
          </div>
          <div className="lg:col-span-5">
            <div className="sp-reveal glass rounded-2xl p-8 md:p-10">
              <h2 className="font-display text-2xl font-medium text-steel-100 md:text-3xl">
                {s.includedTitle}
              </h2>
              <ul className="mt-7 space-y-4">
                {s.included.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm font-light leading-relaxed text-steel-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-ink-900 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <p className="sp-reveal eyebrow mb-10">Recent Work</p>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {s.gallery.map((src, i) => (
              <div key={src} className={`sp-reveal overflow-hidden rounded-2xl ${i % 3 === 0 ? 'row-span-2' : ''}`}>
                <img
                  src={src}
                  alt={`${s.name} — Revive Builders project photo ${i + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-ink-950 py-24 md:py-32">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <h2 className="sp-reveal font-display text-3xl font-medium text-steel-100 md:text-5xl">
            Questions, <span className="italic text-gold-300">answered.</span>
          </h2>
          <div className="mt-12 divide-y divide-steel-100/10 border-y border-steel-100/10">
            {s.faqs.map((f) => (
              <details key={f.q} className="sp-reveal group py-6">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-base font-medium text-steel-100 md:text-lg">
                  {f.q}
                  <span className="text-gold-400 transition-transform duration-300 group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 max-w-2xl text-sm font-light leading-relaxed text-steel-300 md:text-base">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + related */}
      <section className="border-t border-steel-100/[0.07] bg-ink-950 py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6 text-center md:px-10">
          <h2 className="sp-reveal font-display text-4xl font-medium text-steel-100 md:text-6xl">
            Have a property <span className="italic text-gold-300">waiting?</span>
          </h2>
          <p className="sp-reveal mx-auto mt-6 max-w-xl text-base font-light text-steel-300">
            Tell us about it. We'll walk it, scope it, and get you a number you can build a budget around.
          </p>
          <div className="sp-reveal mt-10">
            <Link
              to="/contact"
              className="inline-block bg-gold-500 px-10 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink-950 transition-all duration-500 hover:bg-gold-400"
            >
              Request a Bid
            </Link>
          </div>

          <div className="mt-24 grid grid-cols-1 gap-5 text-left sm:grid-cols-3">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/services/${o.slug}`}
                className="sp-reveal group relative overflow-hidden rounded-2xl border border-steel-100/[0.07] bg-ink-800/60 p-7 transition-all duration-500 hover:-translate-y-1 hover:border-gold-500/40"
              >
                <p className="eyebrow mb-3 !text-[10px]">More Services</p>
                <h3 className="font-display text-xl font-medium text-steel-100 group-hover:text-gold-300">
                  {o.name}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
